import { Injectable } from '@angular/core';
import { EMPTY, Observable, catchError, map, switchMap, take, tap, throwError } from 'rxjs';
import { BaseApiService, SessionStorageService } from '@shared/data-access';
import {
  AiLabelingResponse,
  EvidenceDetailsResponse,
  LocationResponse,
  SelfRedactionProgressResponse,
  SuspectTechResponse,
  TagsResponse,
  TranscriptionSummaryResponse,
} from '../models/evidence-response.models';
import { EvidenceRedactionRequest, SetLocationRequest, TrimEvidenceRequest, UpdateEvidenceRequest } from '../models/evidence-request.models';
import { Router } from '@angular/router';
import { EvidenceFacade } from '../state/data-access.facade';
import { AiLabeling, SelfRedactionProgress, TranscriptionSummary } from '../models/evidence.models';
import { EvidenceMicrosoftClarityTrackingService, EvidenceLinkResponse, FileTranscriptionStatus } from '@shared/utils/evidence-management';
import { AppRoutes } from '@shared/utils/navigation';

@Injectable({
  providedIn: 'root',
})
export class EvidenceService {
  private evidenceInStoreUid$: Observable<string>;

  constructor(
    private api: BaseApiService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private evidenceDetailsFacade: EvidenceFacade,
    private evidenceMicrosoftClarityTrackingService: EvidenceMicrosoftClarityTrackingService
  ) {
    this.evidenceInStoreUid$ = this.evidenceDetailsFacade.getEvidenceUid().pipe(take(1));
  }

  getEvidenceById(uid: string): Observable<EvidenceDetailsResponse> {
    const evidenceLockCode = this.sessionStorageService.getItem('fileCode');

    let request$: Observable<EvidenceDetailsResponse>;

    if (evidenceLockCode) {
      request$ = this.api.getWithHeaderAppend(`api/files/${uid}`, 'fileCode', evidenceLockCode);
    } else {
      request$ = this.api.get(`api/files/${uid}`);
    }

    return request$.pipe(
      catchError((error) => {
        if (error.status === 403) {
          if (error.error['Message'] === 'The lock code you entered is incorrect') {
            return throwError(() => error);
          }
          this.router.navigate(['Error', '403'], { queryParams: { backUrl: AppRoutes.Files } });

          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  getPreview(uid: string): Observable<string> {
    const evidenceLockCode = this.sessionStorageService.getItem('fileCode');

    let request$: Observable<string>;

    if (evidenceLockCode) {
      request$ = this.api.getWithHeaderAppend(`api/files/${uid}/preview?isInline=true`, 'fileCode', evidenceLockCode);
    } else {
      request$ = this.api.get(`api/files/${uid}/preview?isInline=true`);
    }

    return request$.pipe(
      catchError((error) => {
        if (error.status === 403) {
          this.router.navigate(['Error', '403'], { queryParams: { backUrl: AppRoutes.Files } });

          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  sendEvidenceRedactionRequest(evidenceRedactionRequest: EvidenceRedactionRequest): Observable<string> {
    return this.evidenceInStoreUid$.pipe(switchMap((uid) => this.api.post<string>(`api/files/${uid}/redactions`, evidenceRedactionRequest)));
  }

  searchTags(searchTerm: string): Observable<TagsResponse[]> {
    return this.api.get(`api/tags?value=${searchTerm}`);
  }

  updateEvidence(requestBody: UpdateEvidenceRequest): Observable<EvidenceDetailsResponse> {
    return this.evidenceInStoreUid$.pipe(switchMap((uid) => this.api.patch<EvidenceDetailsResponse>(`api/files/${uid}`, requestBody)));
  }

  getEvidenceLocation(): Observable<LocationResponse> {
    return this.evidenceInStoreUid$.pipe(switchMap((uid) => this.api.get<LocationResponse>(`api/files/${uid}/locations`)));
  }

  setEvidenceLocation(setLocationRequest: SetLocationRequest): Observable<LocationResponse> {
    let evidenceUid = '';

    return this.evidenceInStoreUid$.pipe(
      switchMap((uid) => {
        evidenceUid = uid;

        return this.api.patch<LocationResponse>(`api/files/${uid}/locations`, setLocationRequest);
      }),
      tap(() => this.evidenceMicrosoftClarityTrackingService.trackSetLocationEventMSClarity(evidenceUid))
    );
  }

  selfRedactionRequest(evidenceUid: string): Observable<number> {
    return this.api.get<number>(`api/files/${evidenceUid}/self-redaction`).pipe(
      catchError((error) => {
        if (error.status === 403) {
          this.router.navigate(['Error', '403'], { queryParams: { backUrl: `${AppRoutes.Files}/${evidenceUid}`, useDefaultMsg: 'true' } });

          return EMPTY;
        }

        if (error.status === 409) {
          this.router.navigate([AppRoutes.Files, evidenceUid]);

          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  selfRedactionProgress(evidenceUid: string): Observable<SelfRedactionProgress> {
    return this.api
      .get<SelfRedactionProgressResponse>(`api/files/${evidenceUid}/self-redaction-progress`)
      .pipe(map((x) => new SelfRedactionProgress(x.FileUid, x.Progress, x.Path)));
  }

  completeSelfRedaction(evidenceUid: string): Observable<number> {
    return this.api.post(`api/files/${evidenceUid}/self-redaction`);
  }

  trimEvidence(evidenceUid: string, request: TrimEvidenceRequest): Observable<void> {
    return this.api.post<void>(`api/files/${evidenceUid}/trim`, request);
  }

  isSuspectTechAvailable(): Observable<boolean> {
    return this.api.get<SuspectTechResponse>('api/services/available').pipe(map((x) => x.IsSuspectTechServiceAvailable));
  }

  getEvidenceLinks(): Observable<EvidenceLinkResponse[]> {
    return this.evidenceInStoreUid$.pipe(switchMap((uid) => this.api.get<EvidenceLinkResponse[]>(`api/files/${uid}/links`)));
  }

  requestTranscription(uid: string): Observable<FileTranscriptionStatus> {
    const evidenceLockCode = this.sessionStorageService.getItem('fileCode');

    if (evidenceLockCode) {
      return this.api.postWithHeaderAppend<FileTranscriptionStatus>(`api/files/${uid}/transcription`, undefined, 'fileCode', evidenceLockCode);
    }

    return this.api.post<FileTranscriptionStatus>(`api/files/${uid}/transcription`);
  }

  getSummaryForEvidenceTranscription(): Observable<TranscriptionSummary> {
    return this.evidenceInStoreUid$.pipe(
      switchMap((uid) => {
        const evidenceLockCode = this.sessionStorageService.getItem('fileCode');

        if (evidenceLockCode) {
          return this.api.getWithHeaderAppend<TranscriptionSummaryResponse>(`api/files/${uid}/summary`, 'fileCode', evidenceLockCode);
        }

        return this.api.get<TranscriptionSummaryResponse>(`api/files/${uid}/summary`);
      }),
      map((response: TranscriptionSummaryResponse) => new TranscriptionSummary(response.Status, response.Summary))
    );
  }

  getAiLabelingForEvidence(uid: string): Observable<AiLabeling> {
    return this.api
      .get<AiLabelingResponse>(`api/files/${uid}/labeling`)
      .pipe(map((response: AiLabelingResponse) => new AiLabeling(response.Status, response.PotentialSummary, response.PotentialCategories)));
  }

  requestSummaryForEvidenceTranscription(): Observable<TranscriptionSummary> {
    return this.evidenceInStoreUid$.pipe(
      switchMap((uid) => {
        const evidenceLockCode = this.sessionStorageService.getItem('fileCode');

        if (evidenceLockCode) {
          return this.api.postWithHeaderAppend<TranscriptionSummaryResponse>(`api/files/${uid}/summary`, undefined, 'fileCode', evidenceLockCode);
        }

        return this.api.post<TranscriptionSummaryResponse>(`api/files/${uid}/summary`);
      }),
      map((response: TranscriptionSummaryResponse) => new TranscriptionSummary(response.Status, response.Summary))
    );
  }

  requestAiLabelingForEvidence(uid: string): Observable<AiLabeling> {
    return this.api
      .post<AiLabelingResponse>(`api/files/${uid}/labeling`)
      .pipe(map((response: AiLabelingResponse) => new AiLabeling(response.Status, response.PotentialSummary, response.PotentialCategories)));
  }

  revertEvidence(isLocked: boolean): Observable<EvidenceDetailsResponse> {
    const evidenceLockCode = this.sessionStorageService.getItem('fileCode');

    if (isLocked && evidenceLockCode) {
      return this.evidenceInStoreUid$.pipe(switchMap((uid) => this.api.patchWithHeaderAppend<EvidenceDetailsResponse>(`api/files/${uid}/revert`, 'fileCode', evidenceLockCode)));
    }

    return this.evidenceInStoreUid$.pipe(switchMap((uid) => this.api.patch<EvidenceDetailsResponse>(`api/files/${uid}/revert`)));
  }
}
